import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import type { SubjectCard } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import { subjectKindMeta } from "@/lib/subjectKinds";
import { WorldViewActivityLine } from "@/components/WorldViewActivityLine";

/**
 * UI-10 on a phone — the world view index as a single column of subject cards.
 *
 * The same read the desktop index draws from, and the same addresses: a card opens
 * `/world-view/<slug>`, which is the subject page on either surface.
 *
 * The desktop's kind filter is deliberately absent. It lives in the navigator panel, which
 * the phone app does not have, and re-inventing it as a row of chips over a list somebody
 * can already scroll would be a control added because there was room rather than because
 * there was a question.
 */
export function PhoneWorldView() {
  const { worldView, worldViewLoading, worldViewProblem, refreshWorldView, worldViewActivity } = useApp();

  // Re-read on arrival rather than poll, for the reason the desktop index gives: synthesis
  // finishes elsewhere and nothing tells this browser when.
  useEffect(() => refreshWorldView(), [refreshWorldView]);

  const subjects = worldView?.subjects ?? [];

  return (
    <div className="flex flex-col gap-2.5 p-4 [animation:omFadeUp_220ms_cubic-bezier(0.4,0,0.2,1)_both]">
      {worldViewProblem && (
        <p role="alert" className="text-[13px] text-[var(--warning)]">
          {worldViewProblem}
        </p>
      )}

      {worldViewLoading ? (
        <p className="text-[13px] text-[var(--text-muted)]">Loading…</p>
      ) : subjects.length === 0 ? (
        <Empty />
      ) : (
        <>
          <p className="flex flex-wrap items-center gap-1 text-[13px] text-[var(--text-muted)]">
            {subjects.length} {subjects.length === 1 ? "subject" : "subjects"} ·{" "}
            {worldViewActivity ? (
              <WorldViewActivityLine activity={worldViewActivity} />
            ) : worldView?.lastRewrittenAt ? (
              `last rewritten ${formatRelative(worldView.lastRewrittenAt)}`
            ) : (
              "not rewritten yet"
            )}
          </p>

          {subjects.map((subject) => (
            <Card key={subject.id} subject={subject} />
          ))}
        </>
      )}
    </div>
  );
}

function Card({ subject }: { subject: SubjectCard }) {
  const meta = subjectKindMeta(subject.kind);
  const Icon = meta.icon;

  return (
    <Link
      to={`/world-view/${subject.slug}`}
      className="flex flex-col gap-1.5 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-3.5 text-left active:border-[var(--border-strong)]"
    >
      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        <Icon size={12} strokeWidth={2.5} className="text-[var(--vl-virtual-soft)]" aria-hidden />
        {meta.label}
      </span>

      <span className="text-[16px] font-bold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
        {subject.title}
      </span>

      {subject.summary && (
        <span className="text-[14px] leading-[1.45] text-[var(--text-secondary)] [text-wrap:pretty]">
          {subject.summary}
        </span>
      )}

      <span className="text-[12px] text-[var(--text-muted)]">
        {subject.beliefCount} {subject.beliefCount === 1 ? "belief" : "beliefs"} · updated{" "}
        {formatRelative(subject.updatedAt)}
      </span>
    </Link>
  );
}

function Empty() {
  return (
    <p className="px-2 py-12 text-center text-[14px] leading-[1.5] text-[var(--text-secondary)] [text-wrap:pretty]">
      Nothing is held yet. Subjects appear once you answer what is waiting under{" "}
      <Link to="/review" className="underline underline-offset-2">
        Needs you
      </Link>
      — saying yes is what asks the system to work out what it means.
    </p>
  );
}
